import React, { useEffect, useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import useAuthStore from "../../stores/useAuthStore";
import styles from "./SignIn.module.scss";

const OATHKEEPER_URL = window._env_.REACT_APP_OATHKEEPER_URL || "http://localhost:4455";

interface OathkeeperResponse {
    status: number;
    data: Record<string, unknown> | null;
    error: string | null;
}

const fetchOathkeeper = async (path: string): Promise<OathkeeperResponse> => {
    try {
        const response = await fetch(`${OATHKEEPER_URL}${path}`, {
            credentials: "include",
        });
        const data = await response.json().catch(() => null);
        return {
            status: response.status,
            data,
            error: response.ok ? null : `HTTP ${response.status}`,
        };
    } catch (error) {
        return {
            status: 0,
            data: null,
            error: error instanceof Error ? error.message : "Network error",
        };
    }
};

const AuthDemo = () => {
    const { t } = useTranslation();
    const session = useAuthStore((state) => state.session);
    const loading = useAuthStore((state) => state.loading);
    const checkSession = useAuthStore((state) => state.checkSession);
    const logout = useAuthStore((state) => state.logout);

    const [sessionsResponse, setSessionsResponse] = useState<OathkeeperResponse | null>(null);
    const [usersResponse, setUsersResponse] = useState<OathkeeperResponse | null>(null);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
        checkSession();
    }, [checkSession]);

    const handleFetchSecured = async () => {
        setFetching(true);
        const [users, sessions] = await Promise.all([
            fetchOathkeeper("/users/me"),
            fetchOathkeeper("/parking-sessions"),
        ]);
        setUsersResponse(users);
        setSessionsResponse(sessions);
        setFetching(false);
    };

    const handleLogout = async () => {
        await logout();
    };

    return (
        <div className={styles._container}>
            <div className={styles.wrapper}>
                <h1>Auth Demo</h1>

                <section style={{ width: "100%" }}>
                    <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
                        Kratos Session (Auth Store)
                    </h2>
                    {loading && <p>{t("auth.loading")}</p>}
                    {!loading && !session && (
                        <div style={cardStyle}>
                            <p style={{ color: "#cc1818" }}>No active session</p>
                            <Link to="/auth/sign-in" style={{ color: "#1d4f8b", fontWeight: 500 }}>
                                Go to Login
                            </Link>
                        </div>
                    )}
                    {!loading && session && (
                        <div style={cardStyle}>
                            <pre style={preStyle}>
                                {JSON.stringify(session, null, 2)}
                            </pre>
                            <button
                                onClick={handleLogout}
                                className={`btn btn-outline-danger`}
                                style={{ borderRadius: 50, marginTop: 12 }}
                            >
                                Logout
                            </button>
                        </div>
                    )}
                </section>

                <section style={{ width: "100%" }}>
                    <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 12 }}>
                        Oathkeeper Secured Endpoints (:4455)
                    </h2>
                    <button
                        onClick={handleFetchSecured}
                        disabled={fetching}
                        className={`btn btn-primary ${styles.submitButton}`}
                    >
                        {fetching ? "Fetching..." : "Fetch /users/me & /sessions"}
                    </button>

                    {sessionsResponse && (
                        <div style={{ ...cardStyle, marginTop: 16 }}>
                            <h3 style={h3Style}>
                                GET /parking-sessions
                                <StatusBadge status={sessionsResponse.status} />
                            </h3>
                            <pre style={preStyle}>
                                {JSON.stringify(sessionsResponse.data, null, 2)}
                            </pre>
                            {sessionsResponse.error && (
                                <p style={{ color: "#cc1818" }}>{sessionsResponse.error}</p>
                            )}
                        </div>
                    )}

                    {usersResponse && (
                        <div style={{ ...cardStyle, marginTop: 16 }}>
                            <h3 style={h3Style}>
                                GET /users/me
                                <StatusBadge status={usersResponse.status} />
                            </h3>
                            <pre style={preStyle}>
                                {JSON.stringify(usersResponse.data, null, 2)}
                            </pre>
                            {usersResponse.error && (
                                <p style={{ color: "#cc1818" }}>{usersResponse.error}</p>
                            )}
                        </div>
                    )}
                </section>

                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                    <Link to="/auth/sign-in" style={linkBtnStyle}>Login</Link>
                    <Link to="/auth/sign-up" style={linkBtnStyle}>Register</Link>
                    <Link to="/" style={linkBtnStyle}>Home</Link>
                </div>
            </div>
        </div>
    );
};

const StatusBadge = ({ status }: { status: number }) => {
    const isOk = status >= 200 && status < 300;
    return (
        <span
            style={{
                marginLeft: 8,
                padding: "2px 8px",
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600,
                background: isOk ? "#d4edda" : "#f8d7da",
                color: isOk ? "#155724" : "#721c24",
            }}
        >
            {status}
        </span>
    );
};

const cardStyle: React.CSSProperties = {
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0px 5.586px 9.044px 0px rgba(0, 51, 102, 0.02), 0px 18.762px 30.377px 0px rgba(0, 51, 102, 0.02), 0px 84px 136px 0px rgba(0, 51, 102, 0.04)",
    width: "100%",
};

const preStyle: React.CSSProperties = {
    background: "#f3f3f5",
    padding: 12,
    borderRadius: 8,
    fontSize: 12,
    overflow: "auto",
    maxHeight: 300,
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
};

const h3Style: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "monospace",
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
};

const linkBtnStyle: React.CSSProperties = {
    color: "#1d4f8b",
    fontWeight: 500,
    textDecoration: "none",
    padding: "8px 16px",
    border: "1px solid #d3d3d3",
    borderRadius: 50,
    fontSize: 14,
};

export default AuthDemo;
